<template>
  <b-row>
    <section class="w-100">
      <div class="header">
        <div class="header-body">
          <div class="row align-items-end">
            <div class="col">
              <h1 class="header-title">Create Bulk Order</h1>
            </div>
          </div>
        </div>
      </div>

      <form @submit.prevent class="row px-3" data-vv-scope="itemForm">
        <div class="col-12 col-sm-9">
          <!-- Shipment Details Card -->
          <b-card class="mb-3" header="Shipment Details">
            <b-row>
              <div class="col-12 col-md-6">
                <b-form-group label="From Warehouse">
                  <b-v-select
                    v-validate="{ required: false }"
                    name="form-input-warehouseId"
                    :state="validateStateScoped('itemForm', 'form-input-warehouseId')"
                    v-model="itemForm.warehouseId"
                    :searchable="true"
                    :options="warehousesList"
                  />
                </b-form-group>
              </div>

              <div class="col-12 col-md-6">
                <b-form-group label="Client">
                  <b-v-select
                    v-validate="{ required: true }"
                    name="form-input-brandId"
                    :state="validateStateScoped('itemForm', 'form-input-brandId')"
                    v-model="itemForm.brandId"
                    :searchable="true"
                    :options="brandsList"
                  />
                </b-form-group>
              </div>

              <b-col cols="12" md="6">
                <b-form-group label="Expected Arrival Date">
                  <b-form-input
                    v-validate="{ required: true }"
                    name="form-input-expectedDate"
                    :state="validateStateScoped('itemForm', 'form-input-expectedDate')"
                    v-model="itemForm.expectedDateTimeUtc"
                    type="date"
                  />
                </b-form-group>
              </b-col>

              <div class="col-12 col-md-6">
                <b-form-group label="Order #">
                  <b-form-input
                    maxlength="28"
                    name="form-input-orderNumber"
                    :state="validateStateScoped('itemForm', 'form-input-orderNumber')"
                    v-validate="{ required: false }"
                    v-model="itemForm.orderNumber"
                  />
                </b-form-group>
              </div>
            </b-row>
          </b-card>

          <!-- Ship To Address Card -->
          <b-card class="mb-3" no-body>
            <template v-slot:header>
              <div class="form-row w-100">
                <div class="col-12 col-md-6">
                  <div class="card-header-small pt-1">Ship To Address</div>
                </div>
                <div class="col-12 col-md-6 align-items-right d-flex" v-if="!has('pm.is_end_partner')">
                  <div class="col-auto p-1">Ship Again to:</div>
                  <b-form-group class="col m-0">
                    <b-v-select
                      :showup="true"
                      v-model="itemForm.shipmentAddressId"
                      :searchable="true"
                      :options="shipmentAddressList"
                    />
                  </b-form-group>
                </div>
              </div>
            </template>
            <div class="card-body">
              <div v-if="hasSelectedExistingAddress" class="alert alert-warning">
                <b>You are sending this shipment to the address picked above.</b>
              </div>
              <div class="form-row">
                <div class="col-6">
                  <b-form-group label="Ship To Name *">
                    <b-form-input
                      id="form-input-shipmentAddress-name"
                      name="form-input-shipmentAddress-name"
                      :state="validateStateScoped('itemForm', 'form-input-shipmentAddress-name')"
                      v-validate="{ required: true }"
                      v-model="itemForm.shipmentAddress.name"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6">
                  <b-form-group label="Country *">
                    <b-v-select
                      v-validate="{ required: true }"
                      id="form-input-shipmentAddress-countryCode"
                      name="form-input-shipmentAddress-countryCode"
                      :state="validateStateScoped('itemForm', 'form-input-shipmentAddress-countryCode')"
                      :disabled="hasSelectedExistingAddress"
                      v-model="itemForm.shipmentAddress.countryCode"
                      :searchable="true"
                      :options="countryCodeList"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6">
                  <b-form-group label="Address Line 1 *">
                    <b-form-input
                      id="form-input-shipmentAddress-addressLine1"
                      name="form-input-shipmentAddress-addressLine1"
                      :state="validateStateScoped('itemForm', 'form-input-shipmentAddress-addressLine1')"
                      v-validate="{ required: true }"
                      v-model="itemForm.shipmentAddress.addressLine1"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6">
                  <b-form-group label="City">
                    <b-form-input
                      v-model="itemForm.shipmentAddress.city"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6">
                  <b-form-group label="Address Line 2">
                    <b-form-input
                      v-model="itemForm.shipmentAddress.addressLine2"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-6 col-md-3">
                  <b-form-group label="State *">
                    <b-v-select
                      v-if="itemForm.shipmentAddress.countryCode == 'US'"
                      v-validate="{ required: true }"
                      name="form-input-shipmentAddress-state"
                      :state="validateStateScoped('itemForm', 'form-input-shipmentAddress-state')"
                      v-model="itemForm.shipmentAddress.state"
                      :searchable="true"
                      :disabled="hasSelectedExistingAddress"
                      :options="USAStateCodeList"
                    />
                    <b-form-input
                      v-else
                      v-validate="{ required: false }"
                      name="form-input-shipmentAddress-state"
                      :state="validateStateScoped('itemForm', 'form-input-shipmentAddress-state')"
                      v-model="itemForm.shipmentAddress.state"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-6 col-md-3">
                  <b-form-group label="Postcode">
                    <b-form-input
                      name="form-input-shipmentAddress-postcode"
                      :state="validateStateScoped('itemForm', 'form-input-shipmentAddress-postcode')"
                      v-validate="{ required: false }"
                      v-model="itemForm.shipmentAddress.postcode"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6">
                  <b-form-group label="Address Line 3">
                    <b-form-input
                      v-model="itemForm.shipmentAddress.addressLine3"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6">
                  <b-form-group label="Contact Email">
                    <b-form-input
                      type="email"
                      v-model="itemForm.shipmentAddress.email"
                      :disabled="hasSelectedExistingAddress"
                    />
                  </b-form-group>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Items To Ship Card -->
          <b-card class="mb-3" header="Items To Ship" no-body>
            <div class="card-body">
              <div class="items">
                <b-table
                  head-variant="light"
                  :fields="shipItemsFields"
                  :items="itemForm.items"
                  :bordered="true"
                >
                  <template v-slot:cell(quantity)="data">
                    <div>{{ data.item.quantity }}</div>
                  </template>
                  <template v-slot:cell(actions)="data">
                    <div class="d-flex">
                      <button class="btn btn-danger btn-sm" @click="RemoveItem(data.item, data.index)">
                        Remove
                      </button>
                    </div>
                  </template>
                  <template v-slot:cell(sku)="data">
                    <CopyText :text="data.item.warehouseProductItem" />
                  </template>
                  <template v-slot:cell(name)="data">
                    <span>{{ GetItemName(data.item.warehouseProductItem) }}</span>
                  </template>
                </b-table>
              </div>
            </div>
            <template v-slot:footer>
              <b-alert v-if="!itemForm.brandId" show>
                <b>Pick the Customer first to get a list of possible warehouse items to choose from.</b>
              </b-alert>
              <b-alert v-else-if="loadingWareItems" variant="warning" show>
                <b>Loading Items..</b>
              </b-alert>
              <div v-else class="form-row">
                <div class="col-4">
                  <b-form-group label="Pick Product">
                    <b-v-select
                      :showup="true"
                      v-model="itemsShipForm.warehouseProductItem"
                      :searchable="true"
                      :options="productsList"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-1">
                  <div class="form-group">
                    <label for="newProdQuantity">Available</label>
                    <money class="form-control" disabled v-model="itemsShipForm.quantityAvailable" min="0" />
                  </div>
                </div>
                <div class="col-6 col-md-3">
                  <b-form-group label="Quantity">
                    <b-input-group>
                      <money class="form-control" v-model="itemsShipForm.quantity" min="0" />
                      <template #append>
                        <b-form-select v-model="itemsShipForm.measurementUnit" :disabled="true" :options="uomList" />
                      </template>
                    </b-input-group>
                  </b-form-group>
                </div>
                <div class="col-md-2">
                  <b-form-group label="Lot Number">
                    <b-form-input class="form-control" v-model="itemsShipForm.reqLotNumber" placeholder="(optional)" />
                  </b-form-group>
                </div>
                <div class="col-6 col-md-2">
                  <b-form-group label=".">
                    <b-button type="button" block variant="primary" @click="AddItem">Add</b-button>
                  </b-form-group>
                </div>
              </div>
            </template>
          </b-card>
        </div>

        <!-- Sidebar -->
        <aside class="col-12 col-sm-3">
          <b-card header="Actions">
            <b-button :disabled="savingItem" variant="primary" block type="submit" @click="SaveForm">
              <span v-if="savingItem">Saving...</span>
              <span v-else>Create Shipment</span>
            </b-button>

            <router-link
              :disabled="savingItem"
              class="mt-2 btn btn-block btn-outline-danger"
              :to="{ name: 'Warehouses.BulkShipments.Index' }"
              exact
            >
              Cancel
            </router-link>
          </b-card>

          <b-card header="Shipping Instructions">
            <div>
              <b-form-group label="Carrier:">
                <b-v-select
                  id="form-input-shipCarrier"
                  name="form-input-shipCarrier"
                  :state="validateStateScoped('itemForm', 'form-input-shipCarrier')"
                  data-vv-as="Extensiv Fulfillment Carrier"
                  v-validate="{ required: false, min: 1 }"
                  v-model="itemForm.tplCarrierCode"
                  :searchable="true"
                  :options="tplCarrierList"
                />
                <b-form-invalid-feedback id="form-input-shipCarrier-feedback">
                  {{ veeErrors.first('form-input-shipCarrier') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
            <div>
              <b-form-group label="Service:">
                <b-v-select
                  id="form-input-shipService"
                  name="form-input-shipService"
                  :state="validateStateScoped('itemForm', 'form-input-shipService')"
                  data-vv-as="Service"
                  v-validate="{ required: false }"
                  v-model="itemForm.tplServiceCode"
                  :searchable="true"
                  :options="tplShipmentServicesList"
                />
                <b-form-invalid-feedback id="form-input-shipService-feedback">
                  {{ veeErrors.first('form-input-shipService') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </b-card>
        </aside>
      </form>
    </section>
  </b-row>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'BulkShipments.Create',
  mixins: [crudMixinGen('bulkShipments'), myCommonDataMixin],

  data: function() {
    return {
      tempSug: '',
      loadingWareItems: false,
      shipmentAddressAll: [],
      wareItemsList: [],
      possibleCarrierList: [],
      possibleServiceList: [],

      itemsShipForm: {
        measurementUnit: 'E',
        warehouseProductId: null,
        warehouseProductItem: null,
        quantity: 0,
        quantityAvailable: 0,
        reqLotNumber: ''
      },

      shipItemsFields: [
        { key: 'sku', label: 'SKU', sortable: false },
        { key: 'name', label: 'Name', sortable: false },
        { key: 'quantity', label: 'Quantity', sortable: false },
        { key: 'reqLotNumber', label: 'Req. Lot Number', sortable: false },
        { key: 'actions' }
      ]
    };
  },

  computed: {
    ...mapGetters({
      countryCodeList: 'getCountryCodeList',
      USAStateCodeList: 'getUSAStateCodeList'
    }),
    ...mapState('bulkShipments', ['orderTypeList']),

    hasSelectedExistingAddress: function() {
      return this.itemForm.shipmentAddressId && this.itemForm.shipmentAddressId !== '-';
    },

    tplCarrierList: function() {
      var itmsList = [];
      var myItems = this.possibleCarrierList;
      itmsList.push({ value: '', text: 'Nothing' });
      myItems
        .filter(function(a) { return a.canShipBulk; })
        .forEach(function(itm) {
          itmsList.push({ value: itm.name, text: itm.name + ' (' + itm.description + ')' });
        });
      return itmsList;
    },

    tplShipmentServicesList: function() {
      var self = this;
      var itmsList = [];
      var myItems = this.possibleServiceList;
      itmsList.push({ value: '', text: 'Nothing' });
      if (this.itemForm.tplCarrierCode) {
        var carr = this.possibleCarrierList.find(function(a) { return a.name === self.itemForm.tplCarrierCode; });
        if (carr) {
          myItems = myItems.filter(function(a) { return a.tplCarrierId === carr.id; });
        } else {
          myItems = [];
        }
      } else {
        myItems = [];
      }
      myItems.forEach(function(itm) {
        itmsList.push({ value: itm.code, text: itm.description + ' (' + itm.code + ')' });
      });
      return itmsList;
    },

    uomList: function() {
      return [
        { value: 'E', text: 'Each' },
        { value: 'BX', text: 'Box' },
        { value: 'FT', text: 'Feet' },
        { value: 'IN', text: 'Inches' },
        { value: 'OZ', text: 'Ounces' },
        { value: 'LB', text: 'Pounds' },
        { value: 'KG', text: 'Kilograms' },
        { value: 'P', text: 'Pallet' }
      ];
    },

    shipmentAddressList: function() {
      var arrs = [];
      arrs.push({ value: '-', text: '- Enter New Address - ' });
      this.shipmentAddressAll.forEach(function(itm) {
        arrs.push({ value: itm.id, text: itm.name + ' (' + itm.addressLine1 + ')' });
      });
      return arrs;
    },

    productsList: function() {
      var self = this;
      var arrs = [];
      this.wareItemsList.forEach(function(itm) {
        arrs.push({
          value: itm.item,
          quantityAvailable: itm.quantityAvail,
          text: itm.item + ' (' + itm.description + ')' + (self.itemForm.warehouseId ? ('- Qty: ' + itm.quantityAvail) : '')
        });
      });
      return arrs;
    }
  },

  watch: {
    'itemForm.shipmentAddressId': function(newVal) {
      if (this.shipmentAddressAll.length > 0) {
        var foundShipTo = this.shipmentAddressAll.find(function(a) { return a.id === newVal; });
        if (foundShipTo) {
          this.itemForm.shipmentAddress = foundShipTo;
        } else {
          this.itemForm.shipmentAddress = {
            id: '',
            name: '',
            countryCode: 'US',
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            state: '',
            postcode: '',
            email: '',
            city: ''
          };
        }
      }
    },

    'itemForm.brandId': function(newVal) {
      this.LoadWareProductsFor(newVal, this.itemForm.warehouseId);
      this.AutoFillNameIfEmpty();
    },

    'itemForm.warehouseId': function(newVal) {
      this.LoadWareProductsFor(this.itemForm.brandId, newVal);
      this.AutoFillNameIfEmpty();
    },

    'itemsShipForm.warehouseProductItem': function(newVal) {
      var self = this;
      this.itemForm.items.forEach(function(a) {
        if (a.warehouseProductItem === newVal) {
          self.RemoveItem(a);
        }
      });
      this.itemsShipForm.quantityAvailable = 0;

      if (newVal) {
        var findProd = this.wareItemsList.find(function(a) { return a.item === newVal; });
        if (findProd) {
          self.itemsShipForm.measurementUnit = findProd.itemUnit;
          self.itemsShipForm.quantityAvailable = findProd.quantityAvail;
        }
      } else {
        this.itemsShipForm.measurementUnit = 'E';
      }
    }
  },

  meta: function() {
    return { title: 'Create Bulk Shipment' };
  },

  mounted: function() {
    this.loadWarehouseCarriersAndServices();
    this.ResetForm();
    this.itemForm.shipmentAddress = {
      id: '',
      name: '',
      countryCode: 'US',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      state: '',
      postcode: '',
      email: '',
      city: ''
    };
    this.itemForm.orderNumber = '';
    if (!this.has('pm.is_end_partner')) {
      this.LoadAddresses();
    }
  },

  methods: {
    loadWarehouseCarriersAndServices: function() {
      var self = this;
      this.$http
        .get('/api/carrierAndServices')
        .then(function(resp) {
          if (resp.data.carriers) {
            self.possibleCarrierList = resp.data.carriers;
            self.possibleServiceList = resp.data.services;
          }
        })
        .catch(function(err) {
          console.error('Error loading carriers and services', err);
        });
    },

    formatDateAsStamp: function(d) {
      var month = '' + (parseInt(d.getMonth()) + 1);
      var day = '' + (d.getDate() + 1);
      var year = d.getFullYear();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;

      var yearStr = (year + '').replace('20', '');
      return [month, day, yearStr].join('');
    },

    RemoveItem: function(item, index) {
      if (index !== undefined) {
        this.itemForm.items = this.itemForm.items.filter(function(a, k) { return k !== index; });
      } else {
        this.itemForm.items = this.itemForm.items.filter(function(a) {
          return a.warehouseProductId !== item.warehouseProductId;
        });
      }
    },

    AddItem: function() {
      var self = this;
      if (
        (this.itemsShipForm.warehouseProductId || this.itemsShipForm.warehouseProductItem) &&
        parseFloat(this.itemsShipForm.quantity) > 0
      ) {
        if (this.itemsShipForm.warehouseProductItem) {
          this.itemForm.items = this.itemForm.items.filter(function(a) {
            return a.item !== self.itemsShipForm.warehouseProductItem;
          });
        } else {
          this.itemForm.items = this.itemForm.items.filter(function(a) {
            return a.warehouseProductId !== self.itemsShipForm.warehouseProductId;
          });
        }

        this.itemForm.items.push({
          manualShipmentId: '-',
          quantity: parseFloat(this.itemsShipForm.quantity),
          warehouseProductItem: this.itemsShipForm.warehouseProductItem,
          reqLotNumber: this.itemsShipForm.reqLotNumber
        });

        this.itemsShipForm = {
          measurementUnit: 'E',
          warehouseProductId: null,
          warehouseProductItem: null,
          quantity: 0,
          quantityAvailable: 0,
          reqLotNumber: ''
        };
      }
    },

    LoadWareProductsFor: function(brandId, warehouseId) {
      var self = this;
      if (this.loadingWareItems) {
        return;
      }
      this.wareItemsList = [];

      if (warehouseId && brandId) {
        this.loadingWareItems = true;
        this.$http
          .post('/api/common/purchaseOrders/pickItems/' + brandId + '/' + warehouseId)
          .then(function(resp) {
            self.wareItemsList = resp.data;
            self.loadingWareItems = false;
          })
          .catch(function(err) {
            self.wareItemsList = [];
            self.loadingWareItems = false;
          });
      } else if (brandId) {
        this.loadingWareItems = true;
        this.$http
          .post('/api/common/purchaseOrders/pickItems/' + brandId)
          .then(function(resp) {
            self.wareItemsList = resp.data;
            self.loadingWareItems = false;
          })
          .catch(function(err) {
            self.wareItemsList = [];
            self.loadingWareItems = false;
          });
      }
    },

    LoadAddresses: function(hndl) {
      var self = this;
      hndl = hndl || function() {};
      this.$http
        .get('/api/common/manualShipments/shipmentAddress')
        .then(function(resp) {
          self.shipmentAddressAll = resp.data.addresses;
          hndl();
        })
        .catch(function(err) {
          console.error('Error loading addresses', err);
        });
    },

    SaveForm: function() {
      var self = this;
      this.savingItem = true;

      this.$validator.validateAll('itemForm').then(function(result) {
        if (!result) {
          self.$swal({ title: 'Form Validation Errors!', icon: 'error' });
          self.savingItem = false;
          return;
        }

        if (self.itemForm.orderNumber) {
          self.itemForm.orderNumber = self.itemForm.orderNumber.toUpperCase().replace(' ', '-');
        }

        if (
          self.itemForm.items.length > 0 &&
          self.itemForm.brandId &&
          (self.itemForm.shipmentAddressId !== '-' ||
            (self.itemForm.shipmentAddress.name && self.itemForm.shipmentAddress.addressLine1))
        ) {
          self.$http
            .post('/api/common/manualShipments/createBulkShipment', self.itemForm)
            .then(function(resp) {
              if (resp.data.id) {
                self.$router.push({
                  name: 'Warehouses.BulkShipments.View',
                  params: { id: resp.data.id }
                });
              } else if (resp.data.errorMessage) {
                self.$swal({ title: resp.data.errorMessage, icon: 'error' });
              } else {
                self.$swal({ title: 'Item Was NOT Saved!', icon: 'error' });
              }
              self.ResetForm();
              self.savingItem = false;
            })
            .catch(function(err) {
              self.savingItem = false;
              if (err.response && err.response.data && err.response.data.errorMessage) {
                self.$swal({ title: err.response.data.errorMessage, icon: 'error' });
              } else {
                self.$swal({ title: 'Item Was NOT Saved!', icon: 'error' });
              }
              console.error('saved po err', err);
            });
        } else {
          self.$swal({ title: 'Fill out required fields!', icon: 'error' });
          self.savingItem = false;
        }
      });
    },

    AutoFillNameIfEmpty: function() {
      if (this.tempSug === this.itemForm.orderNumber) {
        this.AutoFillName();
      }
    },

    AutoFillName: function() {
      var posOrdName = '';
      var brandCode = '';
      var warehouse = this.myWarehouses.find(function(a) { return a.id === this.itemForm.warehouseId; }.bind(this));
      var brand = this.myBrands.find(function(a) { return a.id === this.itemForm.brandId; }.bind(this));

      if (brand) {
        brandCode = brand.code;
      }
      if (warehouse) {
        posOrdName += 'BULKSHIP-' + (brandCode !== '' ? brandCode + '-' : '') + this.formatDateAsStamp(new Date());
      }
      this.tempSug = posOrdName;
      this.itemForm.orderNumber = posOrdName;
    },

    GetItemName: function(prodItem) {
      var nam = '-';
      var prodFound = this.wareItemsList.find(function(a) { return a.item === prodItem; });
      if (prodFound) {
        nam = prodFound.description;
      }
      return nam;
    }
  }
};
</script>
